import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Users, ShieldCheck, ClockCounterClockwise, Warning, XCircle, Buildings, FileText, ChartBar, Eye, Scales, DownloadSimple } from "@phosphor-icons/react";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";

const Stat = ({ icon: Icon, label, value, tone = "default", testId, sub }) => {
  const tones = {
    default: "bg-card border-border",
    amber: "bg-amber-50/60 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900",
    emerald: "bg-emerald-50/60 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900",
    red: "bg-red-50/60 dark:bg-red-950/30 border-red-200 dark:border-red-900",
    slate: "bg-secondary/60 border-border",
  };
  const iconTones = {
    default: "text-muted-foreground",
    amber: "text-amber-700 dark:text-amber-400",
    emerald: "text-emerald-700 dark:text-emerald-400",
    red: "text-red-700 dark:text-red-400",
    slate: "text-slate-700 dark:text-slate-300",
  };
  return (
    <div className={`rounded-2xl border p-5 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1">{label}</div>
          <div className="font-display text-4xl font-bold num-display leading-tight">{value ?? "—"}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
        <Icon size={22} className={iconTones[tone]} />
      </div>
    </div>
  );
};

export default function AdminCompliance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/compliance/dashboard")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  if (!data) return <div className="p-8 text-muted-foreground text-sm">Unable to load compliance dashboard.</div>;

  const inv = data.investors;
  const co = data.companies;
  const act = data.activity;
  const disc = data.disclaimers;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Compliance</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Compliance Dashboard</h1>
          <p className="mt-2 text-muted-foreground text-sm max-w-2xl">
            FCA compliance overview — investor approvals, classification breakdown, company publication status
            and current disclaimer versions.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              const token = localStorage.getItem("ip_token");
              fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/compliance/report.csv`, {
                headers: { Authorization: `Bearer ${token}` },
              }).then((r) => r.blob()).then((b) => {
                const url = URL.createObjectURL(b);
                const a = document.createElement("a");
                a.href = url;
                a.download = "inventya_compliance_report.csv";
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              });
            }}
            data-testid="download-compliance-csv"
          >
            <DownloadSimple size={15} className="mr-1" /> Export CSV report
          </Button>
          <Link to="/admin/disclaimers" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white text-sm font-medium">
            <Scales size={15} /> Disclaimer Management
          </Link>
        </div>
      </div>

      {/* Investors */}
      <div className="mb-2">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">Investors</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat icon={Users} label="Total Investors" value={inv.total} testId="stat-total-investors" />
          <Stat icon={ClockCounterClockwise} label="Pending Approvals" value={inv.pending_approval} tone={inv.pending_approval > 0 ? "amber" : "default"} testId="stat-pending" sub={<Link to="/admin/users" className="text-emerald-700 dark:text-emerald-400 hover:underline">Review users →</Link>} />
          <Stat icon={ShieldCheck} label="Per Se Professionals" value={inv.per_se_professional} tone="emerald" />
          <Stat icon={ShieldCheck} label="Elective Professionals" value={inv.elective_professional} tone="emerald" />
          <Stat icon={Warning} label="Suspended" value={inv.suspended} tone={inv.suspended > 0 ? "red" : "default"} />
          <Stat icon={XCircle} label="Rejected" value={inv.rejected} tone={inv.rejected > 0 ? "red" : "default"} />
          <Stat icon={ClockCounterClockwise} label="Pending Declaration Re-Acceptance" value={inv.pending_declaration_acceptance} tone={inv.pending_declaration_acceptance > 0 ? "amber" : "default"} testId="stat-pending-declaration" />
        </div>
      </div>

      {/* Companies */}
      <div className="mt-8 mb-2">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">Companies</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat icon={Buildings} label="Total Companies" value={co.total} />
          <Stat icon={ShieldCheck} label="Published" value={co.published} tone="emerald" />
          <Stat icon={ClockCounterClockwise} label="Awaiting Compliance Approval" value={co.unpublished_or_review} tone={co.unpublished_or_review > 0 ? "amber" : "default"} />
          <Stat icon={Eye} label="Documents Viewed (24h)" value={act.documents_viewed_24h} tone="slate" />
        </div>
      </div>

      {/* Most viewed + disclaimers */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <ChartBar size={16} className="text-emerald-700 dark:text-emerald-400" />
            <div className="font-display font-bold text-lg">Most Viewed Companies</div>
          </div>
          {act.most_viewed.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">No views recorded yet.</div>
          ) : (
            <div className="space-y-2">
              {act.most_viewed.map((c, i) => (
                <div key={c.id} className="flex items-center gap-3 p-2 rounded hover:bg-secondary/40">
                  <div className="text-xs text-muted-foreground w-5 font-mono">#{i + 1}</div>
                  <div className="flex-1 text-sm font-medium truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground num-display">{c.view_count} views</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <FileText size={16} className="text-emerald-700 dark:text-emerald-400" />
            <div className="font-display font-bold text-lg">Active Disclaimer Versions</div>
          </div>
          {Object.keys(disc).length === 0 ? (
            <div className="text-sm text-muted-foreground italic">No disclaimers active.</div>
          ) : (
            <div className="space-y-2">
              {Object.entries(disc).map(([slot, d]) => (
                <div key={slot} className="flex items-center gap-3 p-2 rounded border border-border/70">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground w-28 shrink-0">{slot.replace("_", " ")}</div>
                  <div className="text-sm font-mono">v{d.version}</div>
                  <div className="text-xs text-muted-foreground ml-auto num-display">Effective {formatDate(d.effective_at)}</div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4">
            <Link to="/admin/disclaimers" className="text-xs text-emerald-700 dark:text-emerald-400 hover:underline">
              Manage disclaimer wording →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
